import type { Encounter, Patient, Resource } from '@medplum/fhirtypes'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { clinicDay } from '#src/dates.ts'
import type { JsonLike } from '#src/json.ts'

export type BindingValue = JsonLike | undefined

export interface DeriveOptions {
  // The day a value is for: the encounter's start when there is one, else
  // today.
  asOf: string
  // Today, on the clinic's calendar (as the encounter day is).
  today: string
}

const DATE_PARTS = /^(\d{4})-(\d{2})-(\d{2})/

function dateParts(
  value: string | undefined
): { year: number; month: number; day: number } | undefined {
  const match = value?.match(DATE_PARTS)
  return match
    ? { year: Number(match[1]), month: Number(match[2]), day: Number(match[3]) }
    : undefined
}

// Whole years from a birth date to a day, both read as calendar days.
export function ageInYears(
  birthDate: string | undefined,
  asOf: string
): number | null {
  const born = dateParts(birthDate)
  const at = dateParts(asOf)
  if (!born || !at) {
    return null
  }
  const beforeBirthday =
    at.month < born.month || (at.month === born.month && at.day < born.day)
  return at.year - born.year - (beforeBirthday ? 1 : 0)
}

// Whole months from a birth date to a day.
function ageInMonths(
  birthDate: string | undefined,
  asOf: string
): number | null {
  const born = dateParts(birthDate)
  const at = dateParts(asOf)
  if (!born || !at) {
    return null
  }
  return (
    (at.year - born.year) * 12 +
    (at.month - born.month) -
    (at.day < born.day ? 1 : 0)
  )
}

// The clinic's day an encounter started on, if it has a start: the same day
// in the browser and on the server, whatever their time zones.
export function encounterStartDay(
  encounter: Encounter | undefined
): string | undefined {
  const start = encounter?.period?.start
  if (!start) {
    return undefined
  }
  return start.length === 10 ? start : clinicDay(start)
}

function patientOf(resource: Resource): Patient | undefined {
  return resource.resourceType === 'Patient' ? resource : undefined
}

const PATIENT_SD = 'http://hl7.org/fhir/StructureDefinition/Patient'
const ADMINISTRATIVE_GENDER = 'http://hl7.org/fhir/administrative-gender'
interface GenderDisplays {
  readonly [gender: string]: string
}

const GENDER_DISPLAY: GenderDisplays = {
  male: 'Male',
  female: 'Female',
  other: 'Other',
  unknown: 'Unknown',
}

// Bindings computed in TypeScript rather than read with FHIRPath: ages need
// the day they are for, which FHIRPath has no way to be given. Notes insert
// them as variables; conditions read them through their alias. Split by
// context so the registry lists each next to the bindings it shares a
// context with.
export const DERIVED_PATIENT_BINDINGS = [
  {
    id: 'participant.age-years',
    label: 'Participant age (years, number)',
    conditionLabel: 'Participant age',
    description:
      'Whole years from the participant’s birth date to the encounter day, or today.',
    context: 'patient',
    resourceType: 'Patient',
    definition: `${PATIENT_SD}#Patient.birthDate`,
    answerTypes: ['integer'],
    kind: 'number',
    alias: 'age',
    formatter: 'integer',
    derive: (resource: Resource, { asOf }: DeriveOptions): BindingValue[] => {
      const age = ageInYears(patientOf(resource)?.birthDate, asOf)
      return age === null ? [] : [age]
    },
  },
  {
    id: 'participant.age',
    label: 'Participant age (as text)',
    description: 'The participant’s age as it reads in a note, such as 82 y.',
    context: 'patient',
    resourceType: 'Patient',
    definition: `${PATIENT_SD}#Patient.birthDate`,
    answerTypes: ['string'],
    kind: 'string',
    derive: (resource: Resource, { asOf }: DeriveOptions): BindingValue[] => {
      const age = ageInYears(patientOf(resource)?.birthDate, asOf)
      return age === null ? [] : [`${age} y`]
    },
  },
  {
    id: 'participant.age-months',
    label: 'Participant age (months, number)',
    conditionLabel: 'Participant age (months)',
    description:
      'Whole months from the participant’s birth date to the encounter day, or today.',
    context: 'patient',
    resourceType: 'Patient',
    definition: `${PATIENT_SD}#Patient.birthDate`,
    answerTypes: ['integer'],
    kind: 'number',
    alias: 'age_months',
    formatter: 'integer',
    derive: (resource: Resource, { asOf }: DeriveOptions): BindingValue[] => {
      const age = ageInMonths(patientOf(resource)?.birthDate, asOf)
      return age === null ? [] : [age]
    },
  },
  {
    id: 'participant.sex',
    label: 'Participant sex',
    description: 'The participant’s administrative gender, as a code.',
    context: 'patient',
    resourceType: 'Patient',
    definition: `${PATIENT_SD}#Patient.gender`,
    answerTypes: ['string'],
    kind: 'coding',
    alias: 'sex',
    formatter: 'coding-display',
    derive: (resource: Resource): BindingValue[] => {
      const gender = patientOf(resource)?.gender
      return gender
        ? [
            {
              system: ADMINISTRATIVE_GENDER,
              code: gender,
              display: GENDER_DISPLAY[gender] ?? gender,
            },
          ]
        : []
    },
  },
] as const

export const DERIVED_ENCOUNTER_BINDINGS = [
  {
    id: 'encounter.days-since',
    label: 'Days since the encounter',
    description: 'Calendar days from the encounter’s start to today.',
    context: 'encounter',
    resourceType: 'Encounter',
    definition:
      'http://hl7.org/fhir/StructureDefinition/Encounter#Encounter.period',
    answerTypes: ['integer'],
    kind: 'number',
    alias: 'days_since_encounter',
    formatter: 'integer',
    derive: (resource: Resource, { today }: DeriveOptions): BindingValue[] => {
      const start =
        resource.resourceType === 'Encounter'
          ? encounterStartDay(resource)
          : undefined
      return start
        ? [differenceInCalendarDays(parseISO(today), parseISO(start))]
        : []
    },
  },
] as const
